/* eslint-disable no-unused-vars */
import { success as successNotif, error as errorNotif } from "react-notification-system-redux";
import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import InputMask from "react-input-mask";
import PropTypes from "prop-types";
import cn from "classnames";

import {
  phoneFormatter,
  phoneCleaner,
  handleResponse,
  getFileExtensionsString,
  getFileExtension,
  checkIfFileAllowed,
  sanitizeFileName,
} from "_helpers";

import { checkIfNumberOfPaymentAttemptsExceeded, increaseBillingCounter, resetBillingCounter } from "_helpers/billing";

import { ordersService, customerService } from "_services";
import { statesConst } from "_constants";
import { customerActions } from "_store/actions";

import { SpinnerBlock } from "widget/Spinner";

const initialPrivacyPackageData = {
  email: "",
  phoneNumber: "",
  address: { address1: "", address2: "", city: "", state: "", zip: "", county: "", country: "US" },
};

const STEPS = {
  init: "",
  loading: "loading",
  default: "default-1",
  privacy1: "privacy-1",
  privacy2: "privacy-2",
  privacyFileAmendment: "privacy-file",
  privacyUploadAmendment: "privacy-upload",
  privacyUpdateInfo: "privacy-update",
  notSubmittedCancelSuccess: "privacy-not-submitted-success",
  submittedCancelSuccess: "privacy-submitted-success",
  uploadSuccess: "privacy-upload-success",
};

const subscriptionNames = Object.freeze({
  tmPrivacyPackage: "Trademark - Privacy Package",
  tmPrivateContactInfo: "Trademark - Private Contact Information",
});

const phoneRegexp = new RegExp(/((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}/);
const emailRegexp = new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i);
const zipRegexp = new RegExp(/^\d{0,5}$/i);

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;

  width: 100%;

  ${({ bold }) =>
    bold &&
    css`
      font-weight: bold;
    `}

  ${({ centerContent }) =>
    centerContent &&
    css`
      justify-content: center;
    `}
`;

const Col = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 24px;

  ${({ smallGap }) =>
    smallGap &&
    css`
      gap: 8px;
    `}
`;

const ColText = styled.div`
  text-align: center;
`;

const Divider = styled.div`
  width: 2px;
  margin: 0 8px;
  background-color: lightgray;
`;

const FileInput = styled.input`
  display: none;
`;
const ClearButton = styled.button`
  min-width: 24px;
  max-width: 24px;
  min-height: 24px;
  max-height: 24px;
  border-radius: 4px;
  font-style: normal;
  font-weight: 700;
  font-size: 12px;
  margin-left: 12px;
  margin-top: 12px;
`;
const FileName = styled.span`
  padding: 12px 24px 12px 12px;
`;
const Wrapper = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid lightgray;
  border-radius: 12px;
  padding: 8px;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      flex-direction: column;
      margin-bottom: 32px;
    `};
`;
const FlexContainer = styled.div`
  display: flex;
`;
const ErrorBox = styled.div`
  padding: 8px;
  background-color: #f0f0f0;
  border-radius: 12px;
`;

export function CancelSubscriptionConfirm({ ordersList, cancelInfo, onCancel }) {
  const dispatch = useDispatch();
  const dataForm = useRef(null);
  const fileInputRef = useRef(null);

  const { productName, id, productId } = cancelInfo || {};

  const { fileTypes } = useSelector(({ constant }) => constant);

  const [step, setStep] = useState(STEPS.init);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validated, setValidated] = useState(false);
  const [wasSubmitted, setWasSubmitted] = useState(false);
  const [newData, setNewData] = useState(initialPrivacyPackageData);
  const [file, setFile] = useState(null);

  const cancelOrder = ordersList.find((order) => {
    const productIdx = order?.products.findIndex((product) => {
      return product.id === productId;
    });
    return productIdx > -1;
  });

  const mainProduct = cancelOrder?.products?.find((product) => product?.code?.main);
  const cancelProduct = cancelOrder?.products?.find((product) => product.id === productId);

  const wasSubmittedToUSPTO = mainProduct?.processingResult.logOfStatuses.some((item) => {
    return item.status === "SubmittedToUSPTO";
  });

  const proofFileType = fileTypes[cancelProduct?.code?.code]?.AMENDMENT_PROOF;
  const proofFileExtensions = getFileExtensionsString(proofFileType?.allowedExtensions);
  const selectedFileExt = getFileExtension(file?.name);
  const isExtensionAllowed = checkIfFileAllowed(selectedFileExt, proofFileType?.allowedExtensions);

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const handleClickFileInput = (e) => {
    e.preventDefault();
    e.stopPropagation();

    fileInputRef.current.click();
  };

  const handleChangeFile = (event) => {
    if (!event.target.files.length) {
      clearFile();
      return;
    }

    const formFileExt = getFileExtension(event.target.files[0]?.name);
    const isCurrentExtensionAllowed = checkIfFileAllowed(formFileExt, proofFileType?.allowedExtensions);
    if (isCurrentExtensionAllowed) {
      const fileSize = event.target.files[0]?.size;
      if (fileSize && fileSize / 1024 / 1024 <= 5) {
        setFile(event.target.files[0]);
      } else {
        dispatch(errorNotif({ title: "Error", message: "File size limit is 5MB" }));
        clearFile();
      }
    } else {
      dispatch(
        errorNotif({ title: "Error", message: "Please select file with correct extension: " + proofFileExtensions })
      );
      clearFile();
    }
  };

  async function handleUploadAmendment(e) {
    try {
      const form = e.currentTarget;

      e.preventDefault();
      e.stopPropagation();

      if (form.checkValidity() !== false) {
        const formData = new FormData();

        if (file) {
          if (isExtensionAllowed) {
            formData.append("file", file, sanitizeFileName(file?.name));
            setLoading(true);
            setStep(STEPS.loading);

            const uploadRequest = await ordersService.postFile(
              cancelProduct.orderId,
              cancelProduct.id,
              formData,
              "AMENDMENT_PROOF",
              true
            );

            setLoading(false);

            if (uploadRequest?.status === 200) {
              setStep(STEPS.uploadSuccess);
            } else {
              setStep(STEPS.privacyUploadAmendment);
              dispatch(
                errorNotif({
                  title: "Error",
                  message:
                    "Something went wrong with uploading your file, please try again later or contact with our agents",
                  autoDismiss: 20,
                })
              );
            }
          } else {
            dispatch(
              errorNotif({
                title: "Error",
                message: "Please select file with correct extension: " + (proofFileExtensions || "any type"),
              })
            );
            clearFile();
          }
        } else {
          dispatch(
            errorNotif({
              title: "Error",
              message: "Please select valid file",
            })
          );
        }
      }
    } catch (response) {
      setLoading(false);
      setError("Unknown error");
      setStep(STEPS.error);
    }
  }

  const handleChangeData = ({ target: { name, value } }) => {
    let newValue = value;

    if (name === "phoneNumber") {
      newValue = phoneCleaner(value);
    }

    setNewData({ ...newData, [name]: newValue });
  };

  const handleChangeAddressData = ({ target: { name, value } }) => {
    let newValue = value;

    if (name === "zip" && !zipRegexp.test(newValue)) {
      return;
    }

    setNewData({ ...newData, address: { ...newData.address, [name]: newValue } });
  };

  function handleCloseModal() {
    if (!loading) {
      setStep(STEPS.init);
      clearFile();
      setError("");
      setNewData(null);
      setWasSubmitted(false);
      setValidated(false);
      onCancel();
    }
  }

  function handleCancelSubscription() {
    dispatch(customerActions.cancelSubscription(id, productId, productName));
    handleCloseModal();
  }

  async function createAmendmentUpsale(orderId, body) {
    try {
      setLoading(false);

      const isNumberOfPaymentAttemptsExceeded = checkIfNumberOfPaymentAttemptsExceeded();

      if (isNumberOfPaymentAttemptsExceeded) {
        throw new Error("Uh oh - your card on file has been declined. Please update your payment method to proceed");
      }

      const request = await ordersService.createDelayedUpsell(orderId, body, true);
      const requestStatus = request?.status;

      handleResponse(request).then((response) => {
        if (requestStatus === 200) {
          if (Array.isArray(response?.products) && response?.products[0]?.paymentStatus === "Declined") {
            increaseBillingCounter();
            setError("Uh oh - your card on file has been declined. Please update your payment method to proceed.");
            setStep(STEPS.error);
          } else {
            resetBillingCounter();
            setStep(STEPS.submittedCancelSuccess);
          }
        } else {
          setError(response?.toString() || "Unknown error");
          setStep("stepError");
        }
      });
    } catch (response) {
      setLoading(false);
      setError(response?.message || response?.toString() || "Unknown error");
      setStep(STEPS.error);
    }
  }

  async function handleCancelWithUpdatePrivacyPackage() {
    try {
      const newPrivacyProductData = {
        ...cancelProduct,
        ...newData,
        virtualAddress: {
          ...cancelProduct?.virtualAddress,
          ...newData?.address,
        },
      };

      const newRegisterProductData = {
        ...mainProduct,
        individual: {
          ...mainProduct?.individual,
          email: newData?.email,
          phone: newData?.phoneNumber,
          address: { ...mainProduct?.address, ...newData.address },
        },
      };

      if (productName === subscriptionNames.tmPrivateContactInfo) {
        delete newPrivacyProductData.virtualAddress;
      }

      const updateRequestPrivacy = await ordersService.putProduct(cancelProduct?.id, newPrivacyProductData, true);
      const updateRequestRegister = await ordersService.putProduct(mainProduct?.id, newRegisterProductData, true);

      if (updateRequestPrivacy?.status === 200 && updateRequestRegister?.status === 200) {
        const cancelRequest = await customerService.postCancelSubscriptions(id, productId, true, true);

        if (cancelRequest?.status === 200) {
          setStep(STEPS.notSubmittedCancelSuccess);
        } else {
          handleResponse(cancelRequest).then((cancelResponse) => {
            setError(`Issue with cancel request. ${cancelResponse ? cancelResponse.toString() : ""}`);
            setStep(STEPS.error);
          });
        }
        setLoading(false);
      } else {
        setLoading(false);
        setStep(STEPS.privacyUpdateInfo);
        dispatch(
          errorNotif({
            title: "Error",
            message: "Something went wrong with updating your data, please try again later or contact with our agents",
          })
        );
      }
    } catch (response) {
      setLoading(false);
      setError(response?.toString() || "Unknown error");
      setStep(STEPS.error);
    }
  }

  function handleSubmit(e) {
    if (e) e.preventDefault();

    if (dataForm.current.checkValidity() === false) {
      if (e) e.stopPropagation();
      setValidated(true);
    } else {
      setLoading(true);
      setStep(STEPS.loading);

      if (step === STEPS.privacyUpdateInfo) {
        handleCancelWithUpdatePrivacyPackage();
      }

      if (step === STEPS.privacyFileAmendment) {
        const newProductData = {
          individual: {
            ...newData,
            email: newData?.email,
            phone: newData?.phoneNumber,
            address: {
              ...newData?.address,
            },
          },
        };

        const body = {
          order: { processingOption: "UPSELL" },
          products: [{ code: { code: "TRADEMARK_Amendment_TrademarkMinor" }, ...newProductData }],
        };

        createAmendmentUpsale(id, body);
      }
    }

    setWasSubmitted(true);
    setValidated(true);
  }

  function renderContent(currentStep) {
    switch (currentStep) {
      case STEPS.default:
        return (
          <Modal centered show backdrop="static" onHide={handleCloseModal} size="md">
            <Modal.Header closeButton>Cancel Subscription</Modal.Header>
            <Modal.Body>Are you sure you wish to cancel your {productName} subscription?</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Nevermind
              </Button>
              <Button variant="danger" onClick={handleCancelSubscription}>
                Yes, please cancel
              </Button>
            </Modal.Footer>
          </Modal>
        );

      case STEPS.privacy1:
        return (
          <Modal centered show backdrop="static" onHide={handleCloseModal} size="md">
            <Modal.Header closeButton>Cancel Subscription</Modal.Header>
            <Modal.Body>
              <div>
                If cancelling {`"${productName}"`} service you will need to update the USPTO with your new contact
                information.
              </div>
              {wasSubmittedToUSPTO && (
                <>
                  <br />
                  <div>
                    We offer amendment services to handle this replacement for you if needed. Would you still like to
                    cancel?
                  </div>
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Nevermind
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  if (wasSubmittedToUSPTO) {
                    setStep(STEPS.privacy2);
                  } else {
                    setStep(STEPS.privacyUpdateInfo);
                  }
                }}
              >
                Yes, please cancel
              </Button>
            </Modal.Footer>
          </Modal>
        );

      case STEPS.privacy2:
        return (
          <Modal centered show backdrop="static" onHide={handleCloseModal} size="md">
            <Modal.Header closeButton>Cancel Subscription</Modal.Header>
            <Modal.Body>
              <Row>
                In order to cancel this service, an amendment must be filed with the state to replace the contact
                information on your filing.
              </Row>
              <br />
              <Row bold centerContent>
                Please, select one of options:
              </Row>
              <br />
              <Row>
                <Col>
                  <ColText>
                    If you have already filed the amendment, please select this option and upload the required proof
                    that you have filed with the state.
                  </ColText>
                  <Button variant="secondary" onClick={() => setStep(STEPS.privacyUploadAmendment)}>
                    Upload Amendment
                  </Button>
                </Col>
                <Divider />
                <Col>
                  <ColText>
                    If you need us to file the amendment for you for <b>$100</b>, select this option.
                  </ColText>
                  <Button variant="success" onClick={() => setStep(STEPS.privacyFileAmendment)}>
                    File My Amendment
                  </Button>
                </Col>
              </Row>
            </Modal.Body>
          </Modal>
        );

      case STEPS.privacyUploadAmendment:
        return (
          <Modal centered show backdrop="static" onHide={handleCloseModal} size="md">
            <Modal.Header closeButton>Cancel Subscription</Modal.Header>
            <form noValidate onSubmit={handleUploadAmendment}>
              <Modal.Body>
                <div>Please upload your Amendment Filing Confirmation</div>
                <br />
                <Wrapper>
                  <Button variant="secondary" onClick={handleClickFileInput}>
                    Select file
                  </Button>
                  <FlexContainer>
                    <FileInput ref={fileInputRef} type="file" onChange={handleChangeFile} />
                    {file && (
                      <ClearButton title="Clear selected file" onClick={() => clearFile()}>
                        X
                      </ClearButton>
                    )}
                    <FileName>{file?.name || "Choose file..."}</FileName>
                  </FlexContainer>
                </Wrapper>
                <div style={{ color: "gray" }}>Max. upload file size: 5MB</div>
              </Modal.Body>
              <Modal.Footer>
                <Button type="submit" variant="success" disabled={!file || loading}>
                  Submit
                </Button>
              </Modal.Footer>
            </form>
          </Modal>
        );

      case STEPS.privacyUpdateInfo:
      case STEPS.privacyFileAmendment:
        return (
          <Modal centered show backdrop="static" onHide={handleCloseModal} size="md">
            <Modal.Header closeButton>Cancel Subscription</Modal.Header>
            <Form noValidate validated={validated} onSubmit={handleSubmit} ref={dataForm}>
              <Modal.Body>
                <div>Please, provide the new contact information to provide to the USPTO</div>
                <div>
                  <hr />
                  <Form.Group className="mb-2">
                    <Form.Label>Phone Number:</Form.Label>
                    <InputMask
                      required
                      name="phoneNumber"
                      className={cn("form-control form-control-sm", {
                        "is-invalid":
                          wasSubmitted &&
                          (!newData?.phoneNumber || !phoneRegexp.test(phoneFormatter(newData?.phoneNumber))),
                      })}
                      type="text"
                      placeholder="(999) 999-9999"
                      mask="(999) 999-9999"
                      pattern="((\(\d{3}\) ?)|(\d{3}-))?\d{3}-\d{4}"
                      value={phoneFormatter(newData?.phoneNumber) || ""}
                      onChange={handleChangeData}
                    />
                    <Form.Control.Feedback type="invalid">Please, provide correct Phone Number</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Email:</Form.Label>
                    <Form.Control
                      required
                      name="email"
                      className={cn("form-control form-control-sm", {
                        "is-invalid": wasSubmitted && (!newData?.email || !emailRegexp.test(newData?.email)),
                      })}
                      size="sm"
                      placeholder="example@mail.com"
                      pattern="^[a-zA-Z0-9_%\.\+\-]+@[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,}$"
                      value={newData?.email || ""}
                      onChange={handleChangeData}
                    />
                    <Form.Control.Feedback type="invalid">Please, provide correct Email</Form.Control.Feedback>
                  </Form.Group>
                  {productName === subscriptionNames.tmPrivacyPackage && (
                    <>
                      <br />
                      <Form.Group className="mb-2">
                        <Form.Label>Address 1:</Form.Label>
                        <Form.Control
                          required
                          name="address1"
                          size="sm"
                          placeholder="Address 1"
                          // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
                          value={newData?.address?.address1 || ""}
                          onChange={handleChangeAddressData}
                        />
                        <Form.Control.Feedback type="invalid">Please, provide correct Address 1</Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>Address 2 (optional):</Form.Label>
                        <Form.Control
                          name="address2"
                          size="sm"
                          placeholder="Address 2"
                          // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
                          value={newData?.address?.address2 || ""}
                          onChange={handleChangeAddressData}
                        />
                        <Form.Control.Feedback type="invalid">Please, provide correct Address 2</Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>City:</Form.Label>
                        <Form.Control
                          required
                          name="city"
                          size="sm"
                          placeholder="City"
                          // pattern="^[a-zA-Z0-9!@#%&'_=;:,<> \+\*\^\|\{\}\.\?\$\(\)\[\]\-\/\\]+$"
                          value={newData?.address?.city || ""}
                          onChange={handleChangeAddressData}
                        />
                        <Form.Control.Feedback type="invalid">Please, provide correct City</Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>State:</Form.Label>
                        <Form.Control
                          as="select"
                          required
                          name="state"
                          size="sm"
                          className=""
                          placeholder="State"
                          value={newData?.address?.state || ""}
                          onChange={handleChangeAddressData}
                        >
                          <option value="">Select a state...</option>
                          {Object.keys(statesConst).map((key) => (
                            <option key={key} value={key}>
                              {statesConst[key]}
                            </option>
                          ))}
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                          Please, select one of State options
                        </Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>ZIP:</Form.Label>
                        <Form.Control
                          required
                          name="zip"
                          size="sm"
                          placeholder="12345"
                          pattern="^\d{5}$"
                          maxLength={5}
                          value={newData?.address?.zip || ""}
                          onChange={handleChangeAddressData}
                        />
                        <Form.Control.Feedback type="invalid">Please, provide correct ZIP</Form.Control.Feedback>
                      </Form.Group>
                      <Form.Group className="mb-2">
                        <Form.Label>County:</Form.Label>
                        <Form.Control
                          required
                          name="county"
                          size="sm"
                          placeholder="County"
                          value={newData?.address?.county || ""}
                          onChange={handleChangeAddressData}
                        />
                        <Form.Control.Feedback type="invalid">Please, provide correct County</Form.Control.Feedback>
                      </Form.Group>
                    </>
                  )}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button type="submit" variant="success" disabled={loading}>
                  Submit
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        );

      case STEPS.submittedCancelSuccess:
      case STEPS.notSubmittedCancelSuccess:
        return (
          <Modal centered show backdrop="static" onHide={() => {}} size="md">
            <Modal.Header>Cancel Subscription</Modal.Header>
            <Modal.Body>
              {step === STEPS.submittedCancelSuccess && (
                <div>Thank you! Your Registered Agent Amendment order has successful been placed.</div>
              )}

              {step === STEPS.notSubmittedCancelSuccess && (
                <div>
                  Thank you! Your contact information has been updated and your {`"${productName}"`} service cancelled.
                </div>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="success"
                onClick={() => {
                  handleCloseModal();
                  dispatch(customerActions.getSubscriptions());
                }}
              >
                OK
              </Button>
            </Modal.Footer>
          </Modal>
        );

      case STEPS.uploadSuccess:
        return (
          <Modal centered show backdrop="static" onHide={() => {}} size="md">
            <Modal.Header>Cancel Subscription</Modal.Header>
            <Modal.Body>
              <div>
                Thank you for your file! Our specialists will review to make sure that the amendment was successfully
                filed and cancel your {`"${productName}"`} Subscription.
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="success"
                onClick={() => {
                  handleCloseModal();
                  dispatch(customerActions.getSubscriptions());
                }}
              >
                OK
              </Button>
            </Modal.Footer>
          </Modal>
        );

      case STEPS.error:
        return (
          <Modal centered show backdrop="static" onHide={handleCloseModal} size="md">
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>
              <ErrorBox>
                <div style={{ color: "red" }}>
                  <b>Error:</b>
                </div>
                <div>{error}</div>
              </ErrorBox>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="success"
                onClick={() => {
                  handleCloseModal();
                  dispatch(customerActions.getSubscriptions());
                }}
              >
                OK
              </Button>
            </Modal.Footer>
          </Modal>
        );

      case STEPS.loading:
        return (
          <Modal centered show backdrop="static" onHide={() => {}} size="md">
            <Modal.Header>Cancel Subscription</Modal.Header>
            <Modal.Body>
              <SpinnerBlock />
            </Modal.Body>
          </Modal>
        );

      default:
        return (
          <Modal centered show backdrop="static" onHide={() => {}} size="md">
            <Modal.Header>Cancel Subscription</Modal.Header>
            <Modal.Body>
              <SpinnerBlock />
            </Modal.Body>
          </Modal>
        );
    }
  }

  useEffect(() => {
    switch (productName) {
      case subscriptionNames.tmPrivacyPackage:
      case subscriptionNames.tmPrivateContactInfo:
        setStep(STEPS.privacy1);
        break;
      default:
        setStep(STEPS.default);
        break;
    }
  }, [productName]);

  useEffect(() => {
    dispatch(customerActions.getPaymentMethods());
  }, [dispatch]);

  return renderContent(step);
}

CancelSubscriptionConfirm.propTypes = {
  ordersList: PropTypes.array,
  cancelInfo: PropTypes.shape({
    productName: PropTypes.string,
    id: PropTypes.string,
    productId: PropTypes.string,
  }),
  onCancel: PropTypes.func,
};
